/**
 * The words a tile's own filters are read in.
 *
 * A filter on a member dimension stores KEYS — a customer's uuid, not its name
 * — so a chip built from the rule alone would say "Customer is 3f2a9c1e-…". This
 * resolves each key the same way the filter bar's own chips do: through
 * `batchedLookup`, many keys per request, under the same query key — so a label
 * the bar already fetched is not fetched again, and the two cannot name one
 * customer differently.
 *
 * A key that resolves to nothing stays its own label: a saved filter can hold a
 * member who has since logged nothing this viewer can see, and the raw key is
 * intact where "Unknown" would not be.
 */
import type { FilterNode, ReportModel } from '@clockwise/semantic-core';
import { findDimension, isFilterGroup } from '@clockwise/semantic-core';
import { useQueries } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { batchedLookup } from '@/components/reports/report-filter-control';
import { queryKeys } from '@/lib/query-keys';

/** Every (dimension, key) pair a list filter names, top level and inside groups. */
function memberKeys(model: ReportModel, nodes: readonly FilterNode[]) {
  const out: { field: string; key: string }[] = [];
  const visit = (node: FilterNode) => {
    if (isFilterGroup(node)) {
      node.children.forEach(visit);
      return;
    }
    // Dimensions only: a measure's list is numbers, which read as themselves.
    if (node.value?.kind !== 'list' || !findDimension(model, node.field)) return;
    for (const key of node.value.values) out.push({ field: node.field, key });
  };
  nodes.forEach(visit);
  return out;
}

export function useFilterLabels(
  model: ReportModel | null | undefined,
  nodes: readonly FilterNode[],
  window: { from: string; to: string },
): (field: string, key: string) => string {
  const pairs = useMemo(() => (model ? memberKeys(model, nodes) : []), [model, nodes]);

  // ONE batcher per dimension, so every key of one field shares a request.
  const lookups = useMemo(() => {
    const map = new Map<string, ReturnType<typeof batchedLookup>>();
    if (!model) return map;
    for (const { field } of pairs) {
      if (!map.has(field)) map.set(field, batchedLookup(model.id, field, window.from, window.to));
    }
    return map;
  }, [model, pairs, window.from, window.to]);

  const results = useQueries({
    queries: pairs.map(({ field, key }) => ({
      queryKey: queryKeys.analytics.dimensionValue(model?.id ?? '', field, {
        from: window.from,
        to: window.to,
        key,
      }),
      queryFn: () => {
        const lookup = lookups.get(field);
        return lookup ? lookup(key) : Promise.resolve({ data: { key, label: key } });
      },
      staleTime: 5 * 60_000,
      enabled: !!model,
    })),
  });

  const labels = useMemo(() => {
    const map = new Map<string, string>();
    pairs.forEach(({ field, key }, i) => {
      const label = results[i]?.data?.data.label;
      if (label) map.set(`${field}\u0000${key}`, label);
    });
    return map;
  }, [pairs, results]);

  return useCallback(
    (field: string, key: string) => labels.get(`${field}\u0000${key}`) ?? key,
    [labels],
  );
}
